import { api } from "@/lib/api";
import { toast } from "sonner";
import { useState } from "react";
import { Check, ShieldCheck, RotateCcw, Lock } from "lucide-react";
import { Link } from "react-router-dom";
import { VoiceSample } from "@/components/VoiceSample";

const PLANS = [
  { lookup: "pro_yearly", label: "Annual", price: "$149", per: "per year", featured: true, badge: "BEST VALUE", sub: "Yearly gets you 4 months free", reassure: "Renews yearly, cancel anytime.", renew: "Subscription renews yearly at $149.00 until you cancel." },
  { lookup: "pro_monthly", label: "Monthly", price: "$19", per: "per month", note: "Cancel anytime", renew: "Subscription renews monthly at $19.00 until you cancel." },
];

const PERKS = [
  "AI Escape Coach, 24/7, and it knows your quiz.",
  "Full access to personalized paths, not just your top three.",
  "Exit Number calculator and progress tracking.",
  "Weekly check ins that update your roadmap.",
  "Save and compare opportunities.",
  "New paths as we ship them.",
];

export default function Paywall() {
  const [loading, setLoading] = useState(null);

  const checkout = async (lookup_key) => {
    setLoading(lookup_key);
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error("Couldn't start checkout"); setLoading(null); }
  };

  return (
    <div className="max-w-3xl mx-auto px-5 pt-6">
      <div className="eyebrow text-[#FD0F10]">PRO ESCAPE PASS</div>
      <h1 className="font-display text-3xl sm:text-4xl font-black mt-1">Go all in. We're not doing this halfway.</h1>
      <p className="text-gray-100 text-sm sm:text-base mt-3 max-w-xl leading-relaxed">
        Every week without the full plan is another Sunday night stomach knot. Another 4am ceiling stare. Enough of that. Pro is us saying <span className="text-white font-bold">we're in this all the way, with you</span>.
      </p>

      <div className="mt-6 card-panel p-5" data-testid="paywall-empathy">
        <div className="eyebrow text-[#FD0F10]">WHAT CHANGES ON PRO</div>
        <ul className="mt-4 space-y-3 text-sm sm:text-base text-gray-100 leading-relaxed list-disc pl-5 marker:text-[#FD0F10]">
          <li>You stop second guessing the path. Your coach knows you and calls the play.</li>
          <li>Your coach doesn't stop at your first dollar. It keeps going as you grow, scaling, marketing, and sales.</li>
          <li>You stop guessing your finish line. The Exit Number gives you the exact day.</li>
          <li>You stop losing momentum. Roadmap, sprints, and weekly nudges keep you moving.</li>
          <li>You stop doing this alone. Someone is in your corner 24/7, pushing.</li>
        </ul>
      </div>

      <VoiceSample className="mt-4" />

      <div className="mt-8 grid sm:grid-cols-2 gap-4">
        {PLANS.map(p => (
          <div key={p.lookup} className={`card-panel p-6 ${p.featured ? "border-[#FD0F10]" : ""}`} data-testid={`plan-${p.lookup}`}>
            {p.badge && <div className="inline-block text-[10px] font-mono-ui uppercase tracking-wider text-white bg-[#FD0F10] px-2 py-0.5 rounded" data-testid={`badge-${p.lookup}`}>{p.badge}</div>}
            <div className="font-mono-ui uppercase text-xs tracking-wider text-gray-400 mt-1">{p.label}</div>
            <div className="mt-2 flex items-baseline gap-1">
              <span className="font-display text-5xl font-black">{p.price}</span>
              <span className="text-gray-400 text-sm">{p.per}</span>
            </div>
            {p.note && <div className="text-xs text-gray-400 mt-1">{p.note}</div>}
            {p.sub && <div className="text-xs text-[#FD0F10] mt-1 font-bold" data-testid={`subline-${p.lookup}`}>{p.sub}</div>}
            <button disabled={loading === p.lookup} onClick={() => checkout(p.lookup)} className="mt-5 w-full hero-accent text-white font-display font-bold py-3 rounded disabled:opacity-60" data-testid={`checkout-${p.lookup}`}>
              {loading === p.lookup ? "Loading…" : "GET PRO"}
            </button>
            {p.reassure && (
              <p className="mt-2 text-center text-[11px] text-gray-400" data-testid={`reassure-${p.lookup}`}>
                {p.reassure}
              </p>
            )}
            {p.renew && (
              <p className="mt-2 text-[11px] text-gray-500 leading-snug" data-testid={`renewal-note-${p.lookup}`}>
                {p.renew}
              </p>
            )}
          </div>
        ))}
      </div>

      <div className="mt-4 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs text-gray-300" data-testid="trust-badges">
        <span className="flex items-center gap-1.5"><ShieldCheck size={14} className="text-[#FD0F10]" /> Cancel anytime</span>
        <span className="flex items-center gap-1.5"><RotateCcw size={14} className="text-[#FD0F10]" /> 7-day refund</span>
        <span className="flex items-center gap-1.5"><Lock size={14} className="text-[#FD0F10]" /> Secure checkout</span>
      </div>

      <p className="mt-4 text-xs text-gray-400 text-center" data-testid="manage-cancel-note">
        Not a fit? Get a full refund within 7 days of your first payment. Cancel anytime. Renewals are non-refundable.
      </p>

      <div className="mt-8 card-panel p-5">
        <div className="eyebrow text-[#FD0F10]">WHAT'S INCLUDED</div>
        <ul className="mt-3 space-y-2">
          {PERKS.map(p => (
            <li key={p} className="flex items-start gap-2 text-sm text-gray-200"><Check className="text-[#FD0F10] mt-0.5" size={16}/>{p}</li>
          ))}
        </ul>
      </div>

      <p className="text-xs text-gray-400 mt-6">Payments handled by Stripe. Every path shows realistic potential income ranges so you can plan with clarity.</p>
      <p className="mt-2 text-[11px] text-gray-500" data-testid="paywall-fineprint">
        Results depend on your effort, time, and other factors. <Link to="/terms" className="underline hover:text-gray-400" data-testid="paywall-terms-link">Read the full terms and disclaimer</Link>.
      </p>
      <div className="mt-4 card-panel p-4 text-sm text-gray-300" data-testid="paywall-referral-hint">
        <span className="text-[#FD0F10] font-bold">Tip.</span> Refer a friend who upgrades and you get <span className="text-white font-bold">thirty days of Pro free</span>. Stackable. See your <a href="/referral" className="underline">referral link</a>.
      </div>
    </div>
  );
}
